import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  useMediaQuery,
  Grid,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import SyncIcon from '@mui/icons-material/Sync';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useAuth } from '../../contexts/AuthContext';
import { hepsiburadaApi, hepsijetApi, mngCargoApi, pazaramaApi, pttavmApi, n11Api, trendyolMpApi, mainApi } from '../../config/apiClient';
import AddIntegrationModal from '../e-commerce/components/AddIntegrationModal';
import EditIntegrationModal from '../e-commerce/components/EditIntegrationModal';
import MarketplaceProductsModal from '../e-commerce/components/MarketplaceProductsModal';
import { useTranslation } from 'react-i18next';

// Platform kategorileri
type MarketplacePlatform = 'trendyol' | 'n11' | 'hepsiburada' | 'amazon' | 'pazarama' | 'pttavm';
type FoodPlatform = 'yemeksepeti' | 'getir' | 'migros_yemek' | 'trendyol_yemek';
type CargoPlatform = 'hepsijet' | 'mng';
type Platform = MarketplacePlatform | FoodPlatform | CargoPlatform;

interface IntegrationData {
  id: number;
  companyId: string;
  branchId?: string | null;
  branchName?: string | null;
  platform: Platform;
  category: 'marketplace' | 'food' | 'cargo';
  isEnabled: boolean;
  apiKey?: string | null;
  apiSecret?: string | null;
  sellerId?: string | null;
  restaurantId?: string | null;
  appSecretKey?: string | null;
  restaurantSecretKey?: string | null;
  isConnected: boolean;
  lastSyncAt?: string | null;
  errorCount: number;
  lastErrorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
  enabled?: boolean;
  status?: {
    connected: boolean;
    lastSync: string;
    errorCount: number;
  };
  credentials?: {
    apiKey?: string;
    apiSecret?: string;
    sellerId?: string;
    restaurantId?: string;
    appSecretKey?: string;
    restaurantSecretKey?: string;
    lastUpdated?: string;
  };
  settings?: {
    categories?: any;
  };
}

const MARKETPLACE_PLATFORMS = [
  { key: 'trendyol' as MarketplacePlatform, label: 'Trendyol', color: '#f27a1a' },
  { key: 'n11' as MarketplacePlatform, label: 'N11', color: '#7b1fa2' },
  { key: 'hepsiburada' as MarketplacePlatform, label: 'Hepsiburada', color: '#ff6000' },
  { key: 'pazarama' as MarketplacePlatform, label: 'Pazarama', color: '#e6007e' },
  { key: 'pttavm' as MarketplacePlatform, label: 'PttAVM', color: '#e30613' },
];

const FOOD_PLATFORMS = [
  { key: 'yemeksepeti' as FoodPlatform, label: 'Yemeksepeti', color: '#e31e24' },
  { key: 'getir' as FoodPlatform, label: 'Getir Food', color: '#5d3ebc' },
  { key: 'migros_yemek' as FoodPlatform, label: 'Migros Food', color: '#ff6600' },
  { key: 'trendyol_yemek' as FoodPlatform, label: 'Trendyol Food', color: '#f27a1a' },
];

const CARGO_PLATFORMS = [
  { key: 'hepsijet' as CargoPlatform, label: 'HepsiJet', color: '#ff6000' },
  { key: 'mng' as CargoPlatform, label: 'DHL (MNG)', color: '#FFCC00' },
];

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(1),
}));

const PlatformIntegrationsSection: React.FC = () => {
  const { t } = useTranslation('salesSystem');
  const { userDetails, branches } = useAuth();
  const isMobile = useMediaQuery('(max-width: 1499px)');
  const [integrations, setIntegrations] = useState<IntegrationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIntegrationForMenu, setSelectedIntegrationForMenu] = useState<IntegrationData | null>(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addModalCategory, setAddModalCategory] = useState<'marketplace' | 'food' | 'cargo'>('marketplace');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationData | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [integrationToDelete, setIntegrationToDelete] = useState<IntegrationData | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false);
  const [selectedIntegrationForProducts, setSelectedIntegrationForProducts] = useState<IntegrationData | null>(null);
  const [testingIntegrationId, setTestingIntegrationId] = useState<number | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const marketplaceIntegrations = integrations.filter(i =>
    i.category === 'marketplace' || MARKETPLACE_PLATFORMS.some(p => p.key === i.platform)
  );
  const foodIntegrations = integrations.filter(i =>
    i.category === 'food' || FOOD_PLATFORMS.some(p => p.key === i.platform)
  );
  const cargoIntegrations = integrations.filter(i =>
    i.category === 'cargo' || CARGO_PLATFORMS.some(p => p.key === i.platform)
  );

  useEffect(() => {
    const fetchCompanyId = async () => {
      if (userDetails?.companyId) {
        setCompanyId(userDetails.companyId);
      } else {
        setError(t('integrationPage.errors.companyInfoNotFound'));
        setLoading(false);
      }
    };
    fetchCompanyId();
  }, [userDetails, t]);

  useEffect(() => {
    if (!companyId) return;
    fetchIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchIntegrations = async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await mainApi.get(`/api/platformintegration/list?companyId=${companyId}`);
      const result = response.data;

      if (!result.success) {
        throw new Error(result.message || t('integrationPage.errors.loadFailed'));
      }

      const allIntegrations: IntegrationData[] = (result.data || []).map((item: any) => ({
        ...item,
        enabled: item.isEnabled,
        status: {
          connected: item.isConnected,
          lastSync: item.lastSyncAt || '',
          errorCount: item.errorCount || 0
        }
      }));

      setIntegrations(allIntegrations);
    } catch (err) {
      console.error('Error while loading integrations:', err);
      setError(t('integrationPage.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddMarketplaceIntegration = () => {
    setAddModalCategory('marketplace');
    setIsAddModalOpen(true);
  };

  const handleAddFoodIntegration = () => {
    setAddModalCategory('food');
    setIsAddModalOpen(true);
  };

  const handleAddCargoIntegration = () => {
    setAddModalCategory('cargo');
    setIsAddModalOpen(true);
  };

  const handleEditIntegration = (integration: IntegrationData) => {
    setSelectedIntegration(integration);
    setIsEditModalOpen(true);
  };

  const handleDeleteIntegration = (integration: IntegrationData) => {
    setIntegrationToDelete(integration);
    setIsDeleteModalOpen(true);
  };

  const handleGetProducts = (integration: IntegrationData) => {
    setSelectedIntegrationForProducts(integration);
    setIsProductsModalOpen(true);
  };

  // Tum pazaryeri platformlari icin tek birlesik baglanti testi.
  // trendyol/n11/hepsiburada/pazarama kendi API'sinde gercek test calistirir;
  // amazon icin backend API olmadigindan genel credential dogrulamasi yapilir.
  const handleTestMarketplaceConnection = async (integration: IntegrationData) => {
    const platform = integration.platform;
    const testApi =
      platform === 'trendyol' ? trendyolMpApi :
      platform === 'n11' ? n11Api :
      platform === 'hepsiburada' ? hepsiburadaApi :
      platform === 'pazarama' ? pazaramaApi :
      platform === 'pttavm' ? pttavmApi : null;

    const label = getPlatformName(platform);

    // Test sonucunu PlatformIntegration kaydina yazip listeyi yeniler; durum rozeti buna gore guncellenir.
    const persistStatus = async (connected: boolean, errorMessage: string | null) => {
      try {
        await mainApi.post('/api/platformintegration/update-connection', {
          id: integration.id,
          isConnected: connected,
          errorMessage
        });
      } catch (persistErr) {
        console.error('Connection status persist error:', persistErr);
      }
    };

    const runGenericValidation = async () => {
      const response = await mainApi.post('/api/platformintegration/update-connection', {
        id: integration.id,
        isConnected: true,
        errorMessage: null
      });
      const result = response.data;
      setSnackbar({
        open: true,
        message: result?.success
          ? `${label} bilgileri doğrulandı.`
          : (result?.message || `${label} bağlantı testi başarısız.`),
        severity: result?.success ? 'success' : 'error'
      });
    };

    setTestingIntegrationId(integration.id);
    try {
      if (testApi) {
        let endpointMissing = false;
        let apiResult: any = null;
        try {
          apiResult = await testApi.post(`/api/${platform}/integration/test`);
        } catch (apiErr: any) {
          // Test endpoint'i henüz deploy edilmemişse (404) eski genel doğrulamaya düş.
          if (apiErr?.response?.status === 404) {
            endpointMissing = true;
          } else {
            const errMsg = apiErr?.response?.data?.Message || apiErr?.response?.data?.message || null;
            await persistStatus(false, errMsg);
            throw apiErr;
          }
        }

        if (endpointMissing) {
          await runGenericValidation();
        } else {
          const payload = apiResult.data;
          const success = payload?.Success ?? payload?.success;
          const msg = payload?.Message || payload?.message || null;
          await persistStatus(!!success, success ? null : msg);
          setSnackbar({
            open: true,
            message: success
              ? `${label} bağlantı testi başarılı.`
              : (msg || `${label} bağlantı testi başarısız.`),
            severity: success ? 'success' : 'error'
          });
        }
      } else {
        await runGenericValidation();
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.Message || err?.response?.data?.message || `${label} bağlantı testi sırasında hata oluştu.`,
        severity: 'error'
      });
    } finally {
      setTestingIntegrationId(null);
      fetchIntegrations();
    }
  };

  const handleTestHepsijetConnection = async (integration: IntegrationData) => {
    if (integration.platform !== 'hepsijet') {
      return;
    }

    setTestingIntegrationId(integration.id);
    try {
      const response = await hepsijetApi.post('/api/hepsijet/integration/test');
      const payload = response.data;
      const success = payload?.Success ?? payload?.success;
      if (success) {
        setSnackbar({
          open: true,
          message: 'HepsiJet bağlantı testi başarılı.',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: payload?.Message || payload?.message || 'HepsiJet bağlantı testi başarısız.',
          severity: 'error'
        });
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.Message || err?.response?.data?.message || 'HepsiJet bağlantı testi sırasında hata oluştu.',
        severity: 'error'
      });
    } finally {
      setTestingIntegrationId(null);
    }
  };

  const handleTestMngConnection = async (integration: IntegrationData) => {
    if (integration.platform !== 'mng') {
      return;
    }

    setTestingIntegrationId(integration.id);
    try {
      const response = await mngCargoApi.post('/api/MngCargo/integration/test');
      const payload = response.data;
      const success = payload?.Success ?? payload?.success;
      if (success) {
        setSnackbar({
          open: true,
          message: 'MNG (DHL) bağlantı testi başarılı.',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: payload?.Message || payload?.message || 'MNG bağlantı testi başarısız.',
          severity: 'error'
        });
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.Message || err?.response?.data?.message || 'MNG bağlantı testi sırasında hata oluştu.',
        severity: 'error'
      });
    } finally {
      setTestingIntegrationId(null);
    }
  };

  const confirmDeleteIntegration = async () => {
    if (!integrationToDelete || !companyId) return;

    setDeleting(true);

    try {
      const response = await mainApi.post('/api/platformintegration/delete', {
        id: integrationToDelete.id
      });

      const result = response.data;

      if (!result.success) {
        throw new Error(result.message || t('integrationPage.errors.deleteFailed'));
      }

      setSnackbar({
        open: true,
        message: t('integrationPage.messages.integrationDeleted', { platform: getPlatformName(integrationToDelete.platform) }),
        severity: 'success'
      });

      fetchIntegrations();
      setIsDeleteModalOpen(false);
      setIntegrationToDelete(null);
    } catch (err) {
      console.error('Error while deleting integration:', err);
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : t('integrationPage.errors.deleteFailed'),
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  const getPlatformName = (platform: string) => {
    const platformLabels: Record<string, string> = {
      trendyol: t('integrationPage.platforms.trendyol'),
      n11: t('integrationPage.platforms.n11'),
      hepsiburada: t('integrationPage.platforms.hepsiburada'),
      pazarama: t('integrationPage.platforms.pazarama'),
      pttavm: t('integrationPage.platforms.pttavm'),
      amazon: t('integrationPage.platforms.amazon'),
      yemeksepeti: t('integrationPage.platforms.yemeksepeti'),
      getir: t('integrationPage.platforms.getirFood'),
      migros_yemek: t('integrationPage.platforms.migrosFood'),
      trendyol_yemek: t('integrationPage.platforms.trendyolFood'),
      hepsijet: t('integrationPage.platforms.hepsijet'),
      mng: t('integrationPage.platforms.mng'),
    };
    if (platformLabels[platform]) return platformLabels[platform];
    const allPlatforms = [...MARKETPLACE_PLATFORMS, ...FOOD_PLATFORMS, ...CARGO_PLATFORMS];
    const found = allPlatforms.find(p => p.key === platform);
    return found?.label || platform;
  };

  const getPlatformColor = (platform: string) => {
    const allPlatforms = [...MARKETPLACE_PLATFORMS, ...FOOD_PLATFORMS, ...CARGO_PLATFORMS];
    const found = allPlatforms.find(p => p.key === platform);
    return found?.color || '#1976d2';
  };

  const getBranchName = (branchId: string | null | undefined): string => {
    if (!branchId) return '-';
    const branch = branches.find(b => b.id === branchId);
    return branch?.name || branchId;
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, integration: IntegrationData) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedIntegrationForMenu(integration);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedIntegrationForMenu(null);
  };

  const onIntegrationSaved = () => {
    fetchIntegrations();
  };

  const renderIntegrationTable = (
    integrationList: IntegrationData[],
    title: string,
    icon: React.ReactNode,
    emptyMessage: string,
    showBranchColumn: boolean = false
  ) => (
    <>
      <SectionHeader>
        {icon}
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>
        <Chip
          label={integrationList.length}
          size="small"
          color="primary"
          sx={{ ml: 1 }}
        />
      </SectionHeader>

      {integrationList.length === 0 ? (
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center', mb: 3, bgcolor: 'grey.50' }}>
          <Typography color="text.secondary">
            {emptyMessage}
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {integrationList.map((integration) => (
            <Grid item xs={12} sm={6} key={integration.id}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderLeft: 4,
                  borderLeftColor: getPlatformColor(integration.platform),
                  '&:hover': { boxShadow: 3 }
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={getPlatformName(integration.platform)}
                        sx={{
                          bgcolor: getPlatformColor(integration.platform),
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.8rem'
                        }}
                      />
                      <Chip
                        label={integration.enabled ? t('integrationPage.status.active') : t('integrationPage.status.passive')}
                        color={integration.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, integration)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {showBranchColumn && integration.branchId && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>{t('integrationPage.table.branch')}:</strong> {getBranchName(integration.branchId)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: showBranchColumn ? '30%' : '40%' }}>
                    {t('integrationPage.table.platform')}
                  </TableCell>
                  {showBranchColumn && (
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '30%' }}>
                      {t('integrationPage.table.branch')}
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: showBranchColumn ? '25%' : '45%' }}>
                    {t('integrationPage.table.status')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', textAlign: 'center', width: 80 }}>
                    {t('integrationPage.table.action')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {integrationList.map((integration) => (
                  <TableRow key={integration.id} hover>
                    <TableCell>
                      <Chip
                        label={getPlatformName(integration.platform)}
                        sx={{
                          bgcolor: getPlatformColor(integration.platform),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </TableCell>
                    {showBranchColumn && (
                      <TableCell>
                        <Typography variant="body2">
                          {getBranchName(integration.branchId)}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={(integration.enabled ?? integration.isEnabled) ? t('integrationPage.status.active') : t('integrationPage.status.passive')}
                        color={(integration.enabled ?? integration.isEnabled) ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ p: '6px' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, integration)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </>
  );

  return (
    <Box>
      <HeaderContainer sx={{
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: isMobile ? 2 : 0
      }}>
        <Box sx={{
          display: 'flex',
          gap: 1.5,
          flexDirection: isMobile ? 'column' : 'row',
          width: isMobile ? '100%' : 'auto'
        }}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<StorefrontIcon />}
            onClick={handleAddMarketplaceIntegration}
            fullWidth={isMobile}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {t('integrationPage.actions.addMarketplaceIntegration')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="medium"
            startIcon={<RestaurantIcon />}
            onClick={handleAddFoodIntegration}
            fullWidth={isMobile}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {t('integrationPage.actions.addFoodIntegration')}
          </Button>
          <Button
            variant="contained"
            size="medium"
            startIcon={<LocalShippingIcon />}
            onClick={handleAddCargoIntegration}
            fullWidth={isMobile}
            sx={{ whiteSpace: 'nowrap', bgcolor: '#ff6000', '&:hover': { bgcolor: '#e65600' } }}
          >
            {t('integrationPage.actions.addCargoIntegration')}
          </Button>
        </Box>
      </HeaderContainer>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {renderIntegrationTable(
            marketplaceIntegrations,
            t('integrationPage.sections.marketplaceIntegrations'),
            <StorefrontIcon sx={{ color: 'primary.main' }} />,
            t('integrationPage.empty.marketplace'),
            false
          )}

          {renderIntegrationTable(
            foodIntegrations,
            t('integrationPage.sections.foodSiteIntegrations'),
            <RestaurantIcon sx={{ color: 'secondary.main' }} />,
            t('integrationPage.empty.food'),
            true
          )}

          {renderIntegrationTable(
            cargoIntegrations,
            t('integrationPage.sections.cargoIntegrations'),
            <LocalShippingIcon sx={{ color: '#ff6000' }} />,
            t('integrationPage.empty.cargo'),
            false
          )}
        </>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 180,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {selectedIntegrationForMenu && MARKETPLACE_PLATFORMS.some(p => p.key === selectedIntegrationForMenu.platform) && (
          <MenuItem
            onClick={() => {
              if (selectedIntegrationForMenu) {
                handleGetProducts(selectedIntegrationForMenu);
              }
              handleMenuClose();
            }}
            disabled={!selectedIntegrationForMenu?.enabled}
          >
            <ListItemIcon>
              <GetAppIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText primary={t('integrationPage.actions.fetchProducts')} />
          </MenuItem>
        )}

        {selectedIntegrationForMenu && MARKETPLACE_PLATFORMS.some(p => p.key === selectedIntegrationForMenu.platform) && (
          <MenuItem
            onClick={() => {
              if (selectedIntegrationForMenu) {
                handleTestMarketplaceConnection(selectedIntegrationForMenu);
              }
              handleMenuClose();
            }}
            disabled={testingIntegrationId === selectedIntegrationForMenu.id}
          >
            <ListItemIcon>
              <SyncIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primary={testingIntegrationId === selectedIntegrationForMenu.id ? 'Test ediliyor...' : 'Bağlantıyı Test Et'} />
          </MenuItem>
        )}

        {selectedIntegrationForMenu?.platform === 'mng' && (
          <MenuItem
            onClick={() => {
              if (selectedIntegrationForMenu) {
                handleTestMngConnection(selectedIntegrationForMenu);
              }
              handleMenuClose();
            }}
            disabled={testingIntegrationId === selectedIntegrationForMenu.id}
          >
            <ListItemIcon>
              <SyncIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primary={testingIntegrationId === selectedIntegrationForMenu.id ? 'Test ediliyor...' : 'Bağlantıyı Test Et'} />
          </MenuItem>
        )}

        {selectedIntegrationForMenu?.platform === 'hepsijet' && (
          <MenuItem
            onClick={() => {
              if (selectedIntegrationForMenu) {
                handleTestHepsijetConnection(selectedIntegrationForMenu);
              }
              handleMenuClose();
            }}
            disabled={testingIntegrationId === selectedIntegrationForMenu.id}
          >
            <ListItemIcon>
              <SyncIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primary={testingIntegrationId === selectedIntegrationForMenu.id ? 'Test ediliyor...' : 'Bağlantıyı Test Et'} />
          </MenuItem>
        )}

        <MenuItem onClick={() => {
          if (selectedIntegrationForMenu) {
            handleEditIntegration(selectedIntegrationForMenu);
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary={t('integrationPage.actions.edit')} />
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => {
            if (selectedIntegrationForMenu) {
              handleDeleteIntegration(selectedIntegrationForMenu);
            }
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary={t('integrationPage.actions.delete')} />
        </MenuItem>
      </Menu>

      <AddIntegrationModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        companyId={companyId}
        onSaved={onIntegrationSaved}
        category={addModalCategory}
      />

      {selectedIntegration && (
        <EditIntegrationModal
          open={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedIntegration(null);
          }}
          companyId={companyId}
          integration={selectedIntegration}
          onSaved={onIntegrationSaved}
        />
      )}

      {selectedIntegrationForProducts && (
        <MarketplaceProductsModal
          open={isProductsModalOpen}
          onClose={() => {
            setIsProductsModalOpen(false);
            setSelectedIntegrationForProducts(null);
          }}
          companyId={companyId}
          integration={selectedIntegrationForProducts as any}
        />
      )}

      <Dialog open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
        <DialogTitle>{t('integrationPage.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {integrationToDelete &&
              t('integrationPage.deleteDialog.message', { platform: getPlatformName(integrationToDelete.platform) })
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteModalOpen(false)} disabled={deleting}>
            {t('integrationPage.actions.cancel')}
          </Button>
          <Button
            onClick={confirmDeleteIntegration}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : t('integrationPage.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlatformIntegrationsSection;
